import {
  SimpleReceivingView,
  type ReceivingPack,
  type ReceivingProduct,
  type ReceivingPurchaseOrder,
  type ReceivingSupplier,
} from "@medicorepay/ui";
import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { productService } from "../../services/product.service";
import { productRevivalApi } from "../../services/product-revival-client";
import { supplierService } from "../../services/supplier.service";
import { purchaseOrderService } from "../../services/purchase-order.service";
import { useAuthStore } from "../../stores/authStore";

export function CreateGoodsReceiptPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const purchaseOrderId = searchParams.get("poId")?.trim() || undefined;
  const storeId =
    useAuthStore((state) => state.selectedStoreId) ||
    useAuthStore((state) => state.user?.storeId) ||
    "active-store";

  const loadSuppliers = useCallback(async (): Promise<ReceivingSupplier[]> => {
    const response = await supplierService.getSuppliers({
      isActive: true,
      limit: 100,
    });
    return response.data.map((supplier) => ({
      id: supplier.id,
      name: supplier.name,
      phone: supplier.phone,
    }));
  }, []);

  const searchProducts = useCallback(
    async (search: string): Promise<ReceivingProduct[]> => {
      const response = await productService.getProducts({ search, limit: 20 });
      return (response.data ?? []).map((product: any) => ({
        id: product.id,
        name: product.name,
        sku: product.sku,
        unit: product.unit,
        manufacturer: product.manufacturer,
        purchasePrice: Number(product.purchasePrice ?? 0),
        mrp: Number(product.mrp ?? 0),
        gstPercent: Number(product.gstPercent ?? 0),
      }));
    },
    [],
  );

  const loadProductPacks = useCallback(
    async (productId: string): Promise<ReceivingPack[]> => {
      const packs = await productService.getProductPacks(productId);
      return packs
        .filter((pack) => pack.isActive && pack.isPurchaseUnit)
        .map((pack) => ({
          id: pack.id,
          productId: pack.productId,
          description: pack.packDescription,
          unitsPerPack: pack.unitsPerPack,
          purchasePrice: Number(pack.purchasePrice ?? 0),
          mrp: Number(pack.mrp ?? 0),
        }));
    },
    [],
  );

  const loadPurchaseOrder =
    useCallback(async (): Promise<ReceivingPurchaseOrder> => {
      const order = await purchaseOrderService.getPurchaseOrder(
        purchaseOrderId!,
      );
      return {
        id: order.id,
        number: order.poNumber,
        supplierId: order.supplierId,
        lines: order.items
          .map((item) => ({
            purchaseOrderItemId: item.id,
            product: {
              ...item.product,
              purchasePrice: Number(item.unitPrice),
              mrp: Number(item.mrp ?? item.product.mrp ?? 0),
              gstPercent: Number(item.taxPercent),
            },
            purchasePackId: item.purchasePackId,
            purchasePack: item.purchasePack
              ? {
                  id: item.purchasePack.id,
                  productId: item.productId,
                  description: item.purchasePack.packDescription,
                  unitsPerPack: item.purchasePack.unitsPerPack,
                  purchasePrice: Number(item.unitPrice),
                  mrp: Number(item.mrp ?? item.product.mrp ?? 0),
                }
              : undefined,
            quantity: Math.max(
              0,
              item.quantity - item.receivedQty - item.cancelledQty,
            ),
            freeQuantity: Math.max(
              0,
              item.freeQuantity - item.freeReceivedQty - item.freeCancelledQty,
            ),
            cost: Number(item.unitPrice),
            mrp: Number(item.mrp ?? item.product.mrp ?? 0),
            gstPercent: Number(item.taxPercent),
            discountPercent: Number(item.discountPercent ?? 0),
            schemeDiscountPercent: Number(item.schemeDiscountPercent ?? 0),
          }))
          .filter((item) => item.quantity > 0 || item.freeQuantity > 0),
      };
    }, [purchaseOrderId]);

  return (
    <SimpleReceivingView
      loadSuppliers={loadSuppliers}
      searchProducts={searchProducts}
      loadProductPacks={loadProductPacks}
      loadPurchaseOrder={purchaseOrderId ? loadPurchaseOrder : undefined}
      completeReceipt={(body, idempotencyKey) =>
        productRevivalApi.completeSimpleGoodsReceipt(body, {
          idempotencyKey,
          clientApplication: "web",
        })
      }
      authorizeDirectPurchase={(body, idempotencyKey) =>
        productRevivalApi.authorizeDirectPurchase(body, {
          idempotencyKey,
          clientApplication: "web",
        })
      }
      onBack={() => navigate("/goods-receipts")}
      idempotencyScope={storeId}
    />
  );
}
